import React from 'react';
import { Spinner, Alert, Badge, ListGroup, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const SuggestedAttractionsModal = ({ 
  show, 
  onClose, 
  suggestedAttractions = [], 
  isLoading = false, 
  onAddAttraction,
  isAttractionAdded,
  onRegenerateAttractions
}) => {
  const { t } = useTranslation();
  
  // Group attractions by city
  const groupByCities = () => {
    const citiesMap = {};
    
    // Group attractions by city
    suggestedAttractions.forEach(attraction => {
      let cityName = 'Other';
      
      // Try to extract city from location
      if (attraction.location) {
        if (typeof attraction.location === 'object' && attraction.location.city) {
          cityName = attraction.location.city;
        } else if (typeof attraction.location === 'string') {
          // Try to extract city from string address
          const addressParts = attraction.location.split(',');
          if (addressParts.length > 1) {
            // Assume the city is the second part of the address in most formats
            cityName = addressParts[1].trim();
          }
        }
      }
      
      // Initialize city group if it doesn't exist
      if (!citiesMap[cityName]) {
        citiesMap[cityName] = [];
      }
      
      // Add attraction to its city group
      citiesMap[cityName].push(attraction);
    });
    
    // Convert the map to an array of groups
    return Object.entries(citiesMap).sort((a, b) => {
      // Sort by city name but keep "Other" at the end
      if (a[0] === 'Other') return 1;
      if (b[0] === 'Other') return -1;
      return a[0].localeCompare(b[0]);
    });
  };

  return (
    <div 
      className={`custom-modal ${show ? 'visible' : 'hidden'}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
        visibility: show ? 'visible' : 'hidden',
        opacity: show ? 1 : 0,
        transition: 'visibility 0s, opacity 0.15s linear'
      }}
      onClick={onClose}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '0.3rem',
          width: '90%',
          maxWidth: '800px', 
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
        }}
        onClick={e => e.stopPropagation()} 
      >
        <div className="modal-header" style={{ padding: '1rem', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
          <h5 className="modal-title">{t('planDetail.suggestedAttractions.title')}</h5>
          <button 
            type="button" 
            className="btn-close" 
            onClick={onClose}
          ></button>
        </div>
        <div className="modal-body" style={{ padding: '1rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">{t('planDetail.suggestedAttractions.loading')}</p>
            </div>
          ) : suggestedAttractions.length === 0 ? (
            <div>
              <Alert variant="info">
                <div className="d-flex align-items-center justify-content-between">
                  <span>{t('planDetail.suggestedAttractions.noAttractions')}</span>
                </div>
              </Alert>
              {onRegenerateAttractions && (
                <div className="text-center mt-3">
                  <Button 
                    variant="primary" 
                    onClick={onRegenerateAttractions}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t('planDetail.suggestedAttractions.generating')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        {t('planDetail.suggestedAttractions.generateNew')}
                      </>
                    )}
                  </Button>
                  <p className="text-muted small mt-2 mb-0">
                    {t('planDetail.suggestedAttractions.generateDescription')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="mb-3">
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  {t('planDetail.suggestedAttractions.alreadyAdded')} <Badge bg="success" className="ms-1">{t('planDetail.suggestedAttractions.added')}</Badge> {t('planDetail.suggestedAttractions.alreadyAddedInfo')}
                </Alert>
              </div>
              
              {/* Group attractions by city */}
              {groupByCities().map(([cityName, attractions]) => (
                <div key={cityName} className="mb-4">
                  <h5 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-geo-alt me-2"></i>
                    {cityName}
                    <Badge bg="secondary" pill className="ms-2">{attractions.length}</Badge>
                  </h5>
                  
                  <ListGroup variant="flush">
                    {attractions.map((attraction, index) => {
                      const alreadyAdded = isAttractionAdded(attraction);
                      const itemKey = attraction.id || `${cityName}-${index}`;
                      
                      return (
                        <ListGroup.Item 
                          key={itemKey} 
                          className={`d-flex justify-content-between align-items-start ${alreadyAdded ? 'bg-light border-success' : ''}`}
                        >
                          <div className="d-flex w-100">
                            <div className="ms-2 me-auto flex-grow-1">
                              <div className="fw-bold d-flex justify-content-between align-items-center">
                                <div>
                                  {attraction.name}
                                  {alreadyAdded && (
                                    <Badge bg="success" className="ms-2">{t('planDetail.suggestedAttractions.added')}</Badge>
                                  )}
                                </div>
                                <Button 
                                  variant={alreadyAdded ? "outline-success" : "primary"}
                                  size="sm"
                                  onClick={() => onAddAttraction(attraction)}
                                  disabled={alreadyAdded}
                                  className="ms-2"
                                >
                                  {alreadyAdded ? (
                                    <><i className="bi bi-check"></i> {t('planDetail.suggestedAttractions.added')}</>
                                  ) : (
                                    <>{t('planDetail.suggestedAttractions.add')}</>
                                  )}
                                </Button>
                              </div>
                              <p className="mb-1 small">{attraction.description}</p>
                              <div className="small text-muted">
                                {attraction.location && (
                                  <span className="me-3">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    {typeof attraction.location === 'object' 
                                      ? attraction.location.address 
                                      : attraction.location}
                                  </span>
                                )}
                                {attraction.estimatedDuration && (
                                  <span className="me-3"><i className="bi bi-clock me-1"></i>{attraction.estimatedDuration}</span>
                                )}
                                {attraction.price && (
                                  <span><i className="bi bi-tag me-1"></i>{attraction.price}</span>
                                )}
                              </div>
                                
                              {/* Images container - show all images in a row */}
                              {attraction.images && attraction.images.length > 0 && (
                                <div className="d-flex gap-2 mt-2">
                                  {attraction.images.map((image, imgIndex) => (
                                    image.url && (
                                      <div key={imgIndex} className="position-relative">
                                        <img 
                                          src={image.url} 
                                          alt={`${attraction.name} - view ${imgIndex+1}`}
                                          className="img-thumbnail"
                                          style={{ 
                                            width: '100px', 
                                            height: '75px',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        {/* Attribution info */}
                                        {image.attribution && (
                                          <div className="text-muted small text-center">
                                            <small title={`${image.attribution.name}${image.provider ? ` (${image.provider})` : ''}`}>
                                              by: {image.attribution.name.length > 20 
                                                ? image.attribution.name.substring(0, 20) + '...' 
                                                : image.attribution.name}
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    )
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ padding: '1rem', borderTop: '1px solid #dee2e6', flexShrink: 0 }}>
          <Button variant="secondary" onClick={onClose}>
            {t('planDetail.suggestedAttractions.close')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuggestedAttractionsModal;
